/* Generic slide-in drawer base (icon+title+close bar that never scrolls, a scrollable body,
   and an optional sticky-reveal slot) - extend this for any JS-driven drawer (Compare Models,
   the trust-badge details drawer, a future size chart, etc.) instead of duplicating open/close/
   focus-trap/sticky-reveal logic. Pair with snippets/eko-drawer.liquid for the markup shell.

   snippets/eko-drawer.liquid emits this script's <script> tag itself, once per drawer INSTANCE
   it renders for (Compare Models, the badge drawer, etc. each call {% render 'eko-drawer' %}
   separately) - on a page using more than one kind of drawer, this file loads more than once.
   A second top-level `class EkoDrawer` declaration would throw (classes can't be redeclared),
   breaking that script's execution - guarded the same way a Dawn global.js singleton would be. */
if (!window.EkoDrawer) {
  window.EkoDrawer = class EkoDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      const overlay = this.querySelector('[data-drawer-overlay]');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
    }

    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;

      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => trapFocus(this.querySelector('.drawer__inner')),
        { once: true }
      );

      document.body.classList.add('overflow-hidden');

      const scroll = this.querySelector('[data-drawer-scroll]');
      if (scroll) scroll.scrollTop = 0;
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    // Toggles [data-drawer-sticky]'s .is-visible class once [data-drawer-header] scrolls out of
    // view within [data-drawer-scroll]. Call again after replacing either element's content (the
    // observed node may itself have been replaced).
    observeStickyReveal() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      const root = this.querySelector('[data-drawer-scroll]');
      const target = this.querySelector('[data-drawer-header]');
      const sticky = this.querySelector('[data-drawer-sticky]');
      if (!root || !target || !sticky) return;
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting),
        { root, threshold: 0 }
      );
      this.stickyObserver.observe(target);
    }
  };
}

/* The small drawers built on the shell above. They used to be one file each (custom-badge-drawer.js,
   custom-species-drawer.js, custom-engraving-drawer.js) that every section had to load AFTER this one;
   living here, they exist as soon as the shell does. This file can load more than once per page (every
   snippets/eko-drawer.liquid render emits it), so each tag is only defined the first time. The one
   bigger drawer, Compare Models (fetches + builds a table), stays in custom-compare.js. */
(() => {
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  const Base = window.EkoDrawer;

  /* Trust-badge / callout detail drawer (V2 PDP description badges, shop trust panel). Content is already on
     the page as inert <template data-badge-content-id>s, so open() clones the right one into the body and
     swaps the title bar text. No fetching. */
  define(
    'badge-drawer',
    class BadgeDrawer extends Base {
      open(contentId, titleText, triggeredBy) {
        super.open(triggeredBy);

        const body = this.querySelector('[data-drawer-body]');
        const template = document.querySelector(`template[data-badge-content-id="${contentId}"]`);
        if (body && template) {
          body.innerHTML = '';
          body.appendChild(template.content.cloneNode(true));
        }

        const titleEl = this.querySelector('.eko-drawer__titlebar-text');
        if (titleEl && titleText) titleEl.textContent = titleText;
      }
    }
  );

  /* People/Animals recommendation drawer (species_toggle block). Content is server-rendered into the body; the
     only extra is a close hook so the toggle in custom-product-v2.js can reset to the product's own default. */
  define(
    'species-drawer',
    class SpeciesDrawer extends Base {
      close() {
        super.close();
        this.dispatchEvent(new CustomEvent('eko:species-drawer-closed', { bubbles: true }));
      }
    }
  );

  /* Engraving editor drawer. The shared #engraving-modal (sections/product-engraving.liquid) is moved into
     [data-engraving-drawer-body] by wireEngravingBridge() in custom-product-v2.js, which also opens/closes this
     drawer whenever that modal opens/closes. Closing by any route (X, backdrop, Escape) must also close the
     modal so its own state (typed text, Save button) resets the same way Cancel does. */
  define(
    'engraving-drawer',
    class EngravingDrawer extends Base {
      connectedCallback() {
        // On phones the on-screen keyboard shrinks the VISUAL viewport but not the layout viewport, so a drawer
        // sized to the layout viewport runs underneath the keyboard and iOS scrolls the content up to keep the
        // focused field in view - pushing the engraved-text preview out of sight. Size the drawer to the visual
        // viewport instead (a CSS variable set here) so the whole editor sits above the keyboard.
        const vv = window.visualViewport;
        if (!vv) return;
        const sync = () => {
          this.style.setProperty('--eko-vv-height', `${Math.round(vv.height)}px`);
          if (this.classList.contains('active')) {
            const scroll = this.querySelector('[data-drawer-scroll]');
            if (scroll && scroll.scrollHeight <= scroll.clientHeight + 1) scroll.scrollTop = 0;
            window.scrollTo(0, window.scrollY); // iOS may have scrolled the page behind the drawer
          }
        };
        vv.addEventListener('resize', sync);
        vv.addEventListener('scroll', sync);
        sync();
      }

      close() {
        const wasActive = this.classList.contains('active');
        super.close();
        if (wasActive) window.engravingSvgManager?.closeModal?.();
      }
    }
  );

  /* "Recommendation" drawer shown when engraving is tapped without the diaphragm set that makes it free.
     Content/behavior are driven from openEngravingUnlockModal() in custom-product-v2.js. */
  define('engraving-unlock-drawer', class EngravingUnlockDrawer extends Base {});
})();
